import { EmissionsCategoryEnum } from '@shared/api/types';
import Unit from '@shared/components/content/unit';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { cn, formatSmallNumber } from '@shared/lib/utils';
import { ClassValue } from 'clsx';

interface EmissionBarCellProps {
  /** The emissions value to display as a bar */
  emissions: number;
  /** The maximum emissions value for scaling the bar width */
  maxEmissions: number;
  /** The carbon intensity (emissions / weight) for color calculation */
  carbonIntensity?: number;
  className?: ClassValue;
  /** The emissions category (A-E) for color calculation */
  emissionsCategory?: string;
  /** Whether to show the tooltip on hover */
  showHover?: boolean;
  type: 'processing' | 'packaging' | 'transport' | 'ingredient';
}

// Color mapping based on carbon intensity thresholds
// These match the A-E rating system

export function EmissionBarCell({
  type,
  maxEmissions,
  emissions,
  carbonIntensity,
  className,
  emissionsCategory,
  showHover = true,
}: EmissionBarCellProps) {
  // Calculate the width percentage (0-100%)
  const widthPercent = maxEmissions > 0 ? (emissions / maxEmissions) * 100 : 0;

  return (
    <div className={cn('flex h-full items-center', className)}>
      {showHover ? (
        <SimpleTooltip
          trigger={
            <div
              className="h-4 cursor-pointer rounded-r-md bg-primary-700 opacity-70"
              style={{
                width: `${Math.min(widthPercent, 100)}%`,
                minWidth: widthPercent > 0 ? '8px' : '0px',
                ...(emissionsCategory && {
                  backgroundColor: `var(--rating-${emissionsCategory.toLowerCase()})`,
                }),
              }}
            />
          }
          content={
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium">Carbon Intensity</span>
              <div className="flex items-center">
                {type === 'ingredient' ? (
                  <RatingCloud
                    rating={emissionsCategory as EmissionsCategoryEnum}
                    className="mr-1 size-8"
                  />
                ) : null}

                <span className="flex items-baseline gap-1 text-sm">
                  {carbonIntensity != null &&
                    formatSmallNumber(carbonIntensity)}{' '}
                  <Unit variant="kgCO2e/kg" className="text-xs" />
                </span>
              </div>
            </div>
          }
        />
      ) : (
        <div
          className="h-4 cursor-pointer rounded-r-md bg-primary-700 opacity-70"
          style={{
            width: `${Math.min(widthPercent, 100)}%`,
            minWidth: widthPercent > 0 ? '8px' : '0px',
            ...(emissionsCategory && {
              backgroundColor: `var(--rating-${emissionsCategory.toLowerCase()})`,
            }),
          }}
        />
      )}
    </div>
  );
}
